import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getAddressLabels, getMonitoredAddresses, getPublicLabels, getSanctionedAddresses } from '../services/api';
import './AddressLabel.css';

// One place that knows every saved address label. The provider fetches the label
// list once (per logged-in user) and exposes a resolver; <AddressLabel> and
// renderTextWithAddressLabels consume it so a labelled address shows its name
// everywhere, with the full address on hover. Keyed by user in App so it refetches
// after login. refresh() lets a page re-pull after it saves a new label.
const AddressLabelsContext = createContext({ resolve: () => null, refresh: () => {}, isSanctioned: () => false });

// ONE address can be two different wallets: an EVM address exists on ERC20 and
// BEP20 alike, and we name them separately ("ERC 2nd 0926" / "2nd BEP 0926"). So
// names are held per chain as well as per address, and a caller that knows the
// chain gets THAT chain's name (operator 2026-11-04: an ERC20 transfer showed the
// BEP name). The per-address entry stays as the fallback for a caller with no
// chain, or an address named on another chain only — same owner, same key.
const chainKey = (address, chain) => `${String(chain).toUpperCase()}|${String(address).toLowerCase()}`;

// Pure, so the whole precedence is testable: the chain's own entry, then the
// per-address entry, then a public tag.
export function pickAddressLabel({ byChain, byAddress, publics }, address, chain) {
  if (!address) return null;
  const key = String(address).toLowerCase();
  return (chain && byChain[chainKey(address, chain)]) || byAddress[key] || publics[key] || null;
}

// The chain a free-text description is about ("ERC20 transaction 0x… - to 0x…").
// Only when it names exactly one: a text naming two chains is not this rule's to
// guess, and falls back to the per-address name.
export function chainFromText(text) {
  const found = new Set((String(text || '').match(/\b(TRC20|ERC20|BEP20)\b/g) || []));
  return found.size === 1 ? [...found][0] : null;
}

export function AddressLabelsProvider({ children }) {
  const [map, setMap] = useState({});
  const [chainMap, setChainMap] = useState({});
  // Public tags (e.g. Tronscan "Binance-Hot 6"), lowest precedence. Loaded ONCE
  // per session from the LOCAL cache (GET /blockchain/public-labels) — never
  // fetched per-view. The backend keeps this cache fresh from Tronscan via a
  // routine background scan. Kept separate from `map` so a refresh() of user/own
  // labels never clobbers it, and so a public tag never overrides a user/own label.
  const [publicMap, setPublicMap] = useState({});
  // OFAC-sanctioned addresses (lowercased), loaded once. Used to flag a matching
  // monitored address or transaction counterparty.
  const [sanctioned, setSanctioned] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const [labelsRes, ownRes, publicRes, sanctionsRes] = await Promise.all([
        getAddressLabels().catch(() => ({ data: [] })),
        getMonitoredAddresses().catch(() => ({ data: [] })),
        getPublicLabels().catch(() => ({ data: {} })),
        getSanctionedAddresses().catch(() => ({ data: [] })),
      ]);
      const next = {};
      const nextByChain = {};
      // Counterparty labels first (customer link beats a plain remark).
      const putLabel = (target, key, r) => {
        if (!target[key] || (r.label_type === 'customer' && target[key].label_type !== 'customer')) {
          target[key] = { label: r.label, label_type: r.label_type, customer_name: r.customer_name };
        }
      };
      for (const r of labelsRes?.data || []) {
        if (!r.address) continue;
        putLabel(next, String(r.address).toLowerCase(), r);
        if (r.chain) putLabel(nextByChain, chainKey(r.address, r.chain), r);
      }
      // Our OWN monitored addresses win — an address we monitor is definitively
      // ours and shows as its wallet name, never as a labellable counterparty.
      for (const a of ownRes?.data || []) {
        if (!a.address) continue;
        const own = { label: a.label || null, label_type: 'own', customer_name: null };
        next[String(a.address).toLowerCase()] = own;
        if (a.chain) nextByChain[chainKey(a.address, a.chain)] = own;
      }
      setMap(next);
      setChainMap(nextByChain);
      // Public tags: { "<address>": "Binance-Hot 6" } from the local cache.
      const pub = {};
      const pdata = publicRes?.data || {};
      for (const addr of Object.keys(pdata)) {
        if (pdata[addr]) pub[String(addr).toLowerCase()] = { label: pdata[addr], label_type: 'public' };
      }
      setPublicMap(pub);
      // OFAC-sanctioned set (lowercased for case-insensitive matching).
      const sset = new Set();
      for (const a of sanctionsRes?.data || []) if (a) sset.add(String(a).toLowerCase());
      setSanctioned(sset);
    } catch (e) {
      // Non-fatal: unresolved addresses just render as the raw address.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = useCallback(
    (address, chain) => pickAddressLabel({ byChain: chainMap, byAddress: map, publics: publicMap }, address, chain),
    [chainMap, map, publicMap]
  );

  const isSanctioned = useCallback(
    (address) => (address ? sanctioned.has(String(address).toLowerCase()) : false),
    [sanctioned]
  );

  return (
    <AddressLabelsContext.Provider value={{ resolve, refresh: load, isSanctioned }}>
      {children}
    </AddressLabelsContext.Provider>
  );
}

export function useAddressLabels() {
  return useContext(AddressLabelsContext);
}

// Middle-truncate a long address so unlabelled ones stay readable inline.
export function truncateAddress(a) {
  const s = String(a || '');
  return s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
}

// Render a single address. When it has a saved label, show the name and swap to
// the FULL address in place on hover (instant, no tooltip). When it is unlabelled,
// just show the full address directly — nothing to reveal.
export function AddressLabel({ address, chain = null, className = '' }) {
  const { resolve, isSanctioned } = useAddressLabels();
  const [tip, setTip] = useState(null); // {left, top} while hovered
  if (!address) return null;
  const info = resolve(address, chain);
  const name = info?.label;
  // OFAC-sanctioned addresses get a red warning badge, whether labelled or not.
  const sanctionBadge = isSanctioned(address)
    ? <span className="address-label-sanction" title="This address is on the OFAC sanctions list">⚠ OFAC</span>
    : null;
  // Unlabelled (or one of our own wallets with no name) → show the full address.
  if (!name) {
    return <span className={`address-label ${className}`}>{sanctionBadge}{address}</span>;
  }
  // Our own monitored wallet gets a distinct look so it reads as "ours".
  const own = info.label_type === 'own';
  // A public (Tronscan) tag gets a subtler look, distinguishable from a user label.
  const isPublic = info.label_type === 'public';
  // Show the full address instantly on hover via a fixed-position tooltip
  // rendered to <body> — so it appears immediately (no native-tooltip delay) and
  // is never clipped by a truncated/overflow-hidden container (e.g. the
  // Transaction History description cell).
  const showTip = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ left: r.left + r.width / 2, top: r.top });
  };
  return (
    <>
      {sanctionBadge}
      <span
        className={`address-label address-label--named${own ? ' address-label--own' : ''}${isPublic ? ' address-label--public' : ''} ${className}`}
        onMouseEnter={showTip}
        onMouseLeave={() => setTip(null)}
      >
        {name}
      </span>
      {tip && createPortal(
        <span className="address-label-tip" style={{ left: tip.left, top: tip.top }}>{address}</span>,
        document.body
      )}
    </>
  );
}

// A crypto address is either a TRON (base58, starts with T) or an EVM (0x + 40
// hex) string. Non-global copy for per-token testing (avoids lastIndex state).
const ADDRESS_TEST = /^(T[1-9A-HJ-NP-Za-km-z]{25,40}|0x[a-fA-F0-9]{40})$/;
const ADDRESS_SPLIT = /(T[1-9A-HJ-NP-Za-km-z]{25,40}|0x[a-fA-F0-9]{40})/g;

// Wrap any addresses inside a free-text string with <AddressLabel>, so baked-in
// descriptions like "TRC20 transaction abc… - from T..." show the label with the
// full address on hover. Returns a React node array (strings + <AddressLabel>).
// `chain` when the caller has it, else the chain the text itself names.
export function renderTextWithAddressLabels(text, chain = null) {
  if (!text || typeof text !== 'string') return text;
  const onChain = chain || chainFromText(text);
  const parts = text.split(ADDRESS_SPLIT);
  return parts.map((part, i) =>
    ADDRESS_TEST.test(part)
      ? <AddressLabel key={i} address={part} chain={onChain} />
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// Render a synced-transaction description with LIVE address labels: strip a
// baked "(Label) " that sits right before a full address (a write-time
// denormalized copy that can go stale) so <AddressLabel> shows the CURRENT
// label / public tag (e.g. "Binance-Hot 6") / OFAC-sanction badge instead of a
// stale duplicate. Descriptions with no address (e.g. "Withdrawal fees") pass
// through unchanged. THE one place this is defined — every list that shows a
// tx description (Crypto Balance, Create Order pickers, …) should use it so a
// saved address name shows everywhere, identically.
export function renderTxDescription(desc, chain = null) {
  if (!desc || typeof desc !== 'string') return desc || '-';
  const cleaned = desc.replace(/\([^)]*\)\s+(?=(?:T[1-9A-HJ-NP-Za-km-z]{25,40}|0x[a-fA-F0-9]{40}))/g, '');
  return renderTextWithAddressLabels(cleaned, chain);
}
